import React, { useState, useRef, useEffect } from 'react';
import { Story } from '../types';
import { theme } from '../../../styles/theme';
import styled from 'styled-components';
import { momentsService, MomentAllowance } from '../../../services/SocialFeed/momentsService';
import { captureVideoThumbnail } from '../../../utils/media/captureVideoThumbnail';
import { logger } from '../../../utils/logger';
import { useSubscriptionContext } from '../../../providers/SubscriptionProvider';

const MAX_STORY_DURATION = 15; // 15 seconds
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];

interface StoryUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (story: Story) => void;
  userId: string;
}

const ModalBackdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${theme.spacing.md};
`;

const ModalContainer = styled.div`
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  line-height: 1;
  
  &:hover {
    color: ${theme.colors.text.primary};
  }
`;

const UploadArea = styled.div<{ $isDragging: boolean }>`
  border: 2px dashed ${props => props.$isDragging ? theme.colors.primary : theme.colors.border.light};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.xl};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$isDragging ? theme.colors.background.secondary : 'transparent'};
  
  &:hover {
    border-color: ${theme.colors.primary};
    background: ${theme.colors.background.secondary};
  }
`;

const UploadIcon = styled.div`
  font-size: 3rem;
  margin-bottom: ${theme.spacing.md};
`;

const UploadText = styled.p`
  color: ${theme.colors.text.primary};
  font-size: 1rem;
  margin: ${theme.spacing.xs} 0;
`;

const UploadSubtext = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
  margin: ${theme.spacing.xs} 0;
`;

const VideoPreview = styled.div`
  margin-top: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  background: ${theme.colors.background.secondary};
`;

const PreviewVideo = styled.video`
  width: 100%;
  max-height: 400px;
  display: block;
`;

const VideoInfo = styled.div`
  padding: ${theme.spacing.md};
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  margin-top: ${theme.spacing.md};
`;

const InfoText = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
  margin: ${theme.spacing.xs} 0;
`;

const DurationWarning = styled.div<{ $isValid: boolean }>`
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  margin-top: ${theme.spacing.md};
  background: ${props => props.$isValid ? 'rgba(46, 213, 115, 0.1)' : 'rgba(255, 71, 87, 0.1)'};
  color: ${props => props.$isValid ? '#2ed573' : '#ff4757'};
  font-size: 0.875rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.lg};
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: ${theme.spacing.md};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$variant === 'primary' ? `
    background: ${theme.colors.primary};
    color: white;
    
    &:hover:not(:disabled) {
      background: ${theme.colors.hover.primary};
    }
  ` : `
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.text.primary};
    border: 1px solid ${theme.colors.border.light};
    
    &:hover {
      background: ${theme.colors.background.tertiary};
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

export const StoryUploadModal: React.FC<StoryUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  userId
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [allowance, setAllowance] = useState<MomentAllowance | null>(null);
  const { tier } = useSubscriptionContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // How many Moments are left today, refreshed each time the modal opens so
  // the number is current after posting one earlier in the session.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    momentsService.getMomentAllowance()
      .then((next) => { if (!cancelled) setAllowance(next); })
      .catch(() => { /* the server rejects an over-limit post regardless */ });
    return () => { cancelled = true; };
    // Keyed on tier as well as open: upgrading mid-session raises the cap.
  }, [isOpen, tier]);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes. The preview URL is released here rather
      // than as soon as its metadata loads — it is still the <video> element's
      // source until the modal goes away.
      setVideoUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return null;
      });
      setSelectedFile(null);
      setVideoDuration(0);
      setIsDragging(false);
      setUploadError(null);
    }
  }, [isOpen]);

  const handleFileSelect = (file: File) => {
    // Validate file type
    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      alert('Please select a valid video file (MP4, WebM, or QuickTime)');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      alert(`File is too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
      return;
    }

    setUploadError(null);
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setVideoUrl(url);

    // Read the duration from a throwaway element with its own URL. Revoking
    // the preview's URL here (as this did) can blank the preview, since that
    // same URL is still the visible <video>'s source.
    const probeUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(probeUrl);
      setVideoDuration(video.duration);
    };
    video.onerror = () => window.URL.revokeObjectURL(probeUrl);
    video.src = probeUrl;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleUpload = async () => {
    if (!selectedFile || videoDuration > MAX_STORY_DURATION) {
      return;
    }
    if (!userId) {
      setUploadError('Please sign in again before posting.');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      // Real upload: bytes to Storage, then a user_moments row. This used to
      // fake a one-second delay and hand back a blob URL that died with the
      // tab, so nothing a user posted survived a reload.
      //
      // The thumbnail is a captured first frame, uploaded as its own object —
      // same approach the profile intro video uses — so a grid or feed can show
      // a still without downloading the whole clip.
      const thumbnail = await captureVideoThumbnail(selectedFile);
      const saved = await momentsService.postMoment(
        userId,
        selectedFile,
        videoDuration,
        thumbnail
      );

      onUpload(saved);
      onClose();
    } catch (error: any) {
      logger.error('[MOMENTS] Failed to post moment:', error);
      setUploadError(error?.message || 'Failed to post moment. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const isValidDuration = videoDuration > 0 && videoDuration <= MAX_STORY_DURATION;
  const outOfMoments = !!allowance && !allowance.unlimited && (allowance.remaining ?? 0) <= 0;

  return (
    <ModalBackdrop $isOpen={isOpen} onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Upload Moment</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <UploadArea
          $isDragging={isDragging}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadIcon>📹</UploadIcon>
          <UploadText>Click or drag video here</UploadText>
          <UploadSubtext>Maximum {MAX_STORY_DURATION} seconds</UploadSubtext>
          <UploadSubtext>MP4, WebM, or QuickTime format</UploadSubtext>
        </UploadArea>

        <HiddenInput
          ref={fileInputRef}
          type="file"
          accept="video/*"
          onChange={handleInputChange}
        />

        {videoUrl && (
          <>
            <VideoPreview>
              <PreviewVideo
                ref={videoRef}
                src={videoUrl}
                controls
                onLoadedMetadata={(e) => {
                  const duration = e.currentTarget.duration;
                  setVideoDuration(duration);
                }}
              />
            </VideoPreview>

            <VideoInfo>
              <InfoText>Duration: {videoDuration.toFixed(2)} seconds</InfoText>
              <InfoText>File: {selectedFile?.name}</InfoText>
            </VideoInfo>

            <DurationWarning $isValid={isValidDuration}>
              {videoDuration > MAX_STORY_DURATION
                ? `Video is too long. Maximum duration is ${MAX_STORY_DURATION} seconds.`
                : `✓ Video duration is valid (${videoDuration.toFixed(2)}s / ${MAX_STORY_DURATION}s)`
              }
            </DurationWarning>
          </>
        )}

        {uploadError && (
          <DurationWarning $isValid={false}>{uploadError}</DurationWarning>
        )}

        {allowance && !allowance.unlimited && (
          <DurationWarning $isValid={(allowance.remaining ?? 0) > 0}>
            {(allowance.remaining ?? 0) > 0
              ? `${allowance.remaining} of ${allowance.dailyLimit} Moments left today.`
              : `You have posted all ${allowance.dailyLimit} of today's Moments. More unlock at midnight.`}
          </DurationWarning>
        )}

        <ActionButtons>
          <Button onClick={onClose} disabled={isUploading}>Cancel</Button>
          <Button
            $variant="primary"
            onClick={handleUpload}
            // Disabled at zero remaining as a courtesy; the server refuses the
            // post either way, and its answer is the one that counts.
            disabled={!isValidDuration || isUploading || outOfMoments}
          >
            {isUploading ? 'Posting…' : outOfMoments ? 'No Moments left today' : 'Post Moment'}
          </Button>
        </ActionButtons>
      </ModalContainer>
    </ModalBackdrop>
  );
};

